import { useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'
import ReportModal from './ReportModal'

const DISC_FALLBACK = {
  windsurf: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=600&q=80',
  kite:     'https://images.unsplash.com/photo-1527004013197-933a4b244bcd?w=600&q=80',
  wing:     'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&q=80',
  windfoil: 'https://images.unsplash.com/photo-1471922694854-ff1b63b20054?w=600&q=80',
  multi:    'https://images.unsplash.com/photo-1505118380757-91f5f5632de0?w=600&q=80',
  default:  'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=600&q=80',
}

const DISC_COLORS = {
  windsurf: '#0369a1', kite: '#7c3aed', wing: '#059669',
  windfoil: '#b45309', multi: '#be123c'
}
const DISC_LABELS = {
  windsurf: '🏄 Windsurf', kite: '🪁 Kite', wing: '🪽 Wingfoil',
  windfoil: '⚡ Windfoil', multi: '🌊 Multi'
}
const CAT_LABELS = { club:'Club', school:'School', shop:'Shop', association:'Assoc.', online:'Online' }

export default function ListingCard({ listing, onVote }) {
  const { user } = useAuth()
  const [voted, setVoted]         = useState(listing.user_voted || false)
  const [votes, setVotes]         = useState(listing.vote_count || 0)
  const [voting, setVoting]       = useState(false)
  const [reporting, setReporting] = useState(false)
  const [expanded, setExpanded]   = useState(false)

  const isFeatured = listing.is_featured
  const isVerified = listing.is_verified
  const imgSrc     = listing.image_url || DISC_FALLBACK[listing.discipline] || DISC_FALLBACK.default

  async function handleVote() {
    if (!user) { window.dispatchEvent(new CustomEvent('open-login')); return }
    if (voted || voting) return
    setVoting(true)
    await supabase.from('votes').insert({ listing_id: listing.id, user_id: user.id })
    await supabase.from('listings').update({ vote_count: votes + 1 }).eq('id', listing.id)
    setVoted(true)
    setVotes(v => v + 1)
    setVoting(false)
    if (onVote) onVote(listing.id, votes + 1)
  }

  // Shared link/vote/report row used in both card and modal
  const ActionRow = ({ large = false }) => (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:8 }}>
      <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
        {listing.website_url && (
          <a href={listing.website_url} target="_blank" rel="noreferrer"
            style={{ fontSize: large ? 13 : 12, color:'var(--blue)', fontWeight:500 }}>
            🌐 Website
          </a>
        )}
        {listing.facebook_url && (
          <a href={listing.facebook_url} target="_blank" rel="noreferrer"
            style={{ fontSize: large ? 13 : 12, color:'var(--blue)', fontWeight:500 }}>
            📘 Facebook
          </a>
        )}
        {listing.instagram_url && (
          <a href={listing.instagram_url} target="_blank" rel="noreferrer"
            style={{ fontSize: large ? 13 : 12, color:'var(--blue)', fontWeight:500 }}>
            📸 Instagram
          </a>
        )}
      </div>
      <div style={{ display:'flex', gap:6, alignItems:'center' }}>
        <button
          onClick={e => { e.stopPropagation(); setReporting(true) }}
          title="Report this listing"
          style={{ background:'none', border:'none', cursor:'pointer', color:'var(--gray-300)', fontSize:13, padding:'4px 6px', borderRadius:6, transition:'color .15s' }}
          onMouseEnter={e => e.currentTarget.style.color = '#dc2626'}
          onMouseLeave={e => e.currentTarget.style.color = 'var(--gray-300)'}
        >🚩</button>
        <button
          onClick={e => { e.stopPropagation(); handleVote() }}
          disabled={voted || voting}
          style={{ display:'flex', alignItems:'center', gap:5, background: voted ? 'var(--gray-100)' : 'var(--gray-50)', border:`1.5px solid ${voted ? 'var(--green)' : 'var(--gray-200)'}`, color: voted ? 'var(--green)' : 'var(--gray-500)', borderRadius:8, padding: large ? '6px 14px' : '5px 10px', fontSize: large ? 13 : 12, fontWeight:600, cursor: voted ? 'default' : 'pointer', transition:'all .15s' }}>
          {voted ? '✓' : '▲'} {votes}
        </button>
      </div>
    </div>
  )

  return (
    <>
      {/* ── CARD ── */}
      <div
        onClick={() => setExpanded(true)}
        style={{
          background: '#fff', borderRadius: 14, overflow: 'hidden', cursor: 'pointer',
          boxShadow: isFeatured ? '0 0 0 2px var(--gold), 0 6px 24px rgba(217,119,6,.15)' : 'var(--shadow)',
          transition: 'box-shadow .2s, transform .2s', position: 'relative',
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = isFeatured ? '0 0 0 2px var(--gold), 0 12px 32px rgba(217,119,6,.2)' : 'var(--shadow-lg)' }}
        onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = isFeatured ? '0 0 0 2px var(--gold), 0 6px 24px rgba(217,119,6,.15)' : 'var(--shadow)' }}
      >
        {/* Image */}
        <div style={{ position:'relative', height:180, overflow:'hidden', background:'var(--gray-100)' }}>
          <img
            src={imgSrc} alt={listing.name}
            style={{ width:'100%', height:'100%', objectFit:'cover', display:'block', transition:'transform .4s' }}
            onError={e => { if (e.target.src !== DISC_FALLBACK.default) e.target.src = DISC_FALLBACK[listing.discipline] || DISC_FALLBACK.default }}
          />
          <div style={{ position:'absolute', top:10, left:10, background: isFeatured ? 'var(--gold)' : 'rgba(255,255,255,.93)', backdropFilter:'blur(4px)', fontFamily:'var(--mono)', fontSize:9, fontWeight:700, color: isFeatured ? '#fff' : 'var(--gray-700)', padding:'3px 8px', borderRadius:5, letterSpacing:'.8px', textTransform:'uppercase', boxShadow: isFeatured ? '0 2px 8px rgba(217,119,6,.4)' : '0 2px 6px rgba(0,0,0,.12)' }}>
            {isFeatured ? '⭐ ' : ''}{CAT_LABELS[listing.category] || listing.category}
          </div>
          <div style={{ position:'absolute', top:10, right:10, background: DISC_COLORS[listing.discipline] || 'var(--gray-500)', fontFamily:'var(--mono)', fontSize:9, letterSpacing:'.8px', textTransform:'uppercase', padding:'3px 9px', borderRadius:50, color:'#fff', fontWeight:600 }}>
            {DISC_LABELS[listing.discipline] || listing.discipline}
          </div>
        </div>

        {/* Body */}
        <div style={{ padding:'14px 16px 16px' }}>
          <div style={{ fontWeight:700, fontSize:15, color:'var(--gray-900)', marginBottom:4, lineHeight:1.2 }}>
            {listing.name}{isVerified && <span title="Verified" style={{ marginLeft:6, fontSize:12 }}>✅</span>}
          </div>
          <div style={{ fontSize:12, color:'var(--gray-500)', marginBottom:3, display:'flex', alignItems:'center', gap:4 }}>
            📍 {listing.city}{listing.country ? `, ${listing.country}` : ''}
          </div>
          <div style={{ fontSize:11, color:'var(--gray-400)', marginBottom:10, fontFamily:'var(--mono)' }}>{listing.type_label}</div>
          <p style={{ fontSize:12.5, color:'var(--gray-500)', lineHeight:1.55, marginBottom:12 }}>{listing.description}</p>
          <ActionRow />
        </div>
      </div>

      {/* ── EXPANDED MODAL ── */}
      {expanded && (
        <div
          className="modal-overlay"
          onClick={() => setExpanded(false)}
          style={{ alignItems:'center', padding:'20px' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background:'#fff', borderRadius:20, overflow:'hidden',
              width:'100%', maxWidth:640,
              boxShadow:'0 24px 80px rgba(0,0,0,.3)',
              animation:'slideUp .2s ease',
              maxHeight:'90vh', overflowY:'auto',
              border: isFeatured ? '2px solid var(--gold)' : 'none',
            }}
          >
            {/* Large image */}
            <div className='expand-img' style={{ position:'relative', height:260, overflow:'hidden', background:'var(--gray-100)' }}>
              <img
                src={imgSrc} alt={listing.name}
                style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }}
                onError={e => { if (e.target.src !== DISC_FALLBACK.default) e.target.src = DISC_FALLBACK[listing.discipline] || DISC_FALLBACK.default }}
              />
              {/* Close button */}
              <button
                onClick={() => setExpanded(false)}
                style={{ position:'absolute', top:12, right:12, width:34, height:34, borderRadius:'50%', background:'rgba(0,0,0,.55)', border:'none', cursor:'pointer', color:'#fff', fontSize:18, display:'flex', alignItems:'center', justifyContent:'center', backdropFilter:'blur(4px)', transition:'background .15s' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,.8)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,0,0,.55)'}
              >×</button>
              {/* Badges */}
              <div style={{ position:'absolute', top:12, left:12, display:'flex', gap:6 }}>
                <div style={{ background: isFeatured ? 'var(--gold)' : 'rgba(255,255,255,.93)', backdropFilter:'blur(4px)', fontFamily:'var(--mono)', fontSize:9, fontWeight:700, color: isFeatured ? '#fff' : 'var(--gray-700)', padding:'3px 8px', borderRadius:5, letterSpacing:'.8px', textTransform:'uppercase', boxShadow: isFeatured ? '0 2px 8px rgba(217,119,6,.4)' : 'none' }}>
                  {isFeatured ? '⭐ ' : ''}{CAT_LABELS[listing.category] || listing.category}
                </div>
                <div style={{ background: DISC_COLORS[listing.discipline] || 'var(--gray-500)', fontFamily:'var(--mono)', fontSize:9, letterSpacing:'.8px', textTransform:'uppercase', padding:'3px 9px', borderRadius:50, color:'#fff', fontWeight:600 }}>
                  {DISC_LABELS[listing.discipline] || listing.discipline}
                </div>
              </div>
            </div>

            {/* Modal body */}
            <div className='expand-body' style={{ padding:'22px 26px 26px' }}>
              <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:12, marginBottom:8 }}>
                <h2 className='expand-title' style={{ fontSize:22, fontWeight:800, color:'var(--gray-900)', lineHeight:1.2, letterSpacing:'-.3px' }}>
                  {listing.name}
                  {isVerified && <span title="Verified active listing" style={{ marginLeft:8, fontSize:16 }}>✅</span>}
                </h2>
              </div>

              <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, color:'var(--gray-500)', marginBottom:4 }}>
                📍 <strong style={{ color:'var(--gray-700)' }}>{listing.city}{listing.country ? `, ${listing.country}` : ''}</strong>
              </div>

              {listing.address && (
                <div style={{ fontSize:12, color:'var(--gray-400)', marginBottom: listing.type_label ? 4 : 16, paddingLeft:18 }}>
                  {listing.address}
                </div>
              )}

              {listing.type_label && (
                <div style={{ fontSize:12, color:'var(--gray-400)', fontFamily:'var(--mono)', marginBottom:16 }}>
                  {listing.type_label}
                </div>
              )}

              <p style={{ fontSize:14, color:'var(--gray-600)', lineHeight:1.7, marginBottom:20 }}>
                {listing.description}
              </p>

              <div style={{ borderTop:'1px solid var(--gray-100)', paddingTop:16 }}>
                <ActionRow large />

              </div>
            </div>
          </div>
        </div>
      )}

      {/* Report modal */}
      {reporting && (
        <ReportModal
          targetId={listing.id}
          targetType="listing"
          targetName={listing.name}
          onClose={() => setReporting(false)}
        />
      )}

    </>
  )
}
